/**
 * UI-4 Task 2 — TwilioCredentialsForm.
 *
 * The credential-entry form for connecting a Twilio SMS channel. Hosted inside
 * `TwilioProviderCard` (Task 3), which owns the API call and passes `onSubmit`.
 *
 * Forms framework (plan §0.F)
 * ---------------------------
 * Built with `<ZodForm>` + `FormText` from `apps/web/src/components/forms/`,
 * per the §0.F forms-framework standard — NOT the raw-`useState` snippet in the
 * Task 2 plan text (that snippet predates §0.F; §0.F wins). `FormText` already
 * supports `type='password'`, so the only piece outside the framework is the
 * show/hide toggle: that is local *UI* state (`tokenVisible`), not a form
 * field, so a `useState` for it does not violate §0.F.
 *
 * E.164
 * -----
 * The `defaultFromHandle` regex is reused from `utils/e164.ts` (`isValidE164`)
 * rather than inlined, matching `ContactDrawer`.
 */
import { useState, type JSX } from 'react'
import { z } from 'zod'
import { useFormContext } from 'react-hook-form'
import { Button } from 'primereact/button'
import { ZodForm, FormText } from '../../forms/index.js'
import { isValidE164 } from '../../../utils/e164.js'

// ---------------------------------------------------------------------------
// Public interface — FIXED. Task 3's TwilioProviderCard consumes this contract.
// ---------------------------------------------------------------------------

export interface TwilioCredentialsFormProps {
  onSubmit: (body: {
    accountSid: string
    authToken: string
    defaultFromHandle: string
  }) => Promise<void>
  submitting: boolean
}

// ---------------------------------------------------------------------------
// Zod schema — the three credential validations.
// ---------------------------------------------------------------------------

const ACCOUNT_SID_REGEX = /^AC[a-zA-Z0-9]{32}$/
const AUTH_TOKEN_REGEX = /^[a-zA-Z0-9]{32}$/

const CredentialsSchema = z.object({
  accountSid: z
    .string()
    .trim()
    .regex(ACCOUNT_SID_REGEX, 'Account SID must start with "AC" followed by 32 alphanumeric characters'),
  authToken: z
    .string()
    .trim()
    .regex(AUTH_TOKEN_REGEX, 'Auth Token must be exactly 32 alphanumeric characters'),
  defaultFromHandle: z
    .string()
    .trim()
    .refine(isValidE164, 'Enter a valid phone number in E.164 format (e.g. +15551234567)'),
})

type CredentialsValues = z.infer<typeof CredentialsSchema>

// ---------------------------------------------------------------------------
// Module-level default values — hoisted so the object is not reconstructed on
// every render.
// ---------------------------------------------------------------------------

const DEFAULT_VALUES: CredentialsValues = {
  accountSid: '',
  authToken: '',
  defaultFromHandle: '',
}

// ---------------------------------------------------------------------------
// Submit button — reads RHF validity + isSubmitting so it disables on an
// invalid form AND during the brief window between click and the parent's
// `submitting` prop becoming true (prevents double-submit).
// ---------------------------------------------------------------------------

function ConnectButton({ submitting }: { submitting: boolean }): JSX.Element {
  const {
    formState: { isValid, isSubmitting },
  } = useFormContext()
  return (
    <Button
      type="submit"
      label={submitting ? 'Connecting…' : 'Connect'}
      loading={submitting}
      disabled={submitting || isSubmitting || !isValid}
    />
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TwilioCredentialsForm({
  onSubmit,
  submitting,
}: TwilioCredentialsFormProps): JSX.Element {
  // UI-only state — controls the auth-token show/hide toggle, not a form field.
  const [tokenVisible, setTokenVisible] = useState(false)

  return (
    <div className="w-full">
      <ZodForm
        schema={CredentialsSchema}
        defaultValues={DEFAULT_VALUES}
        onSubmit={onSubmit}
      >
        <FormText
          name="accountSid"
          label="Account SID"
          required
          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          helpText="Found in your Twilio Console — starts with “AC”."
        />

        {/* Auth token + show/hide toggle. The toggle flips FormText's `type`. */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <FormText
              name="authToken"
              label="Auth Token"
              type={tokenVisible ? 'text' : 'password'}
              required
              placeholder="••••••••••••••••••••••••••••••••"
            />
          </div>
          <Button
            type="button"
            icon={tokenVisible ? 'pi pi-eye-slash' : 'pi pi-eye'}
            aria-label={tokenVisible ? 'Hide auth token' : 'Show auth token'}
            onClick={() => setTokenVisible((v) => !v)}
            className=""
          />
        </div>

        <FormText
          name="defaultFromHandle"
          label="Default From Number"
          required
          placeholder="+15551234567"
          helpText="The Twilio phone number messages are sent from (E.164 format)."
        />

        <ConnectButton submitting={submitting} />
      </ZodForm>
    </div>
  )
}
